import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  message,
  Spin,
  Typography,
  Space,
  Select,
  TimePicker,
  Radio,
  ConfigProvider,
  Row,
  Col,
  Modal,
  Table,
  Tag,
  DatePicker,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { DataScienceJobApi, DataScienceJob } from "../../api/DataScienceJobApi";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";
import { BrightMarketApi } from "../../api/BrightMarketApi";
import {
  searchOrganizations,
  getOrganizations,
  getArticlesByName as searchArticles,
  getCategoriesFiltered,
  getClientSegmentsFiltered,
} from "../../api/Services";
import { getSelectedOrganizationId, hasWorkingOrganization } from "../../api/Helper";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import { getDataScienceJobTypes } from "./dataScienceJobTypes";
import {
  buildTaskProfileSelectOptions,
  getRecommendedTaskProfile,
  getTaskProfileBaseLabel,
  isHighResourceTaskProfile,
  TaskProfileValue,
} from "./taskProfileHelper";
import moment, { Moment } from "moment";
import esES from "antd/es/locale/es_ES";

const { Title, Text } = Typography;

interface JobTypeConfig {
  displayName: string;
  description: string;
  parameters: Record<string, any>;
  parameterFields: ParameterField[];
}

interface ParameterField {
  name: string;
  label: string;
  type:
    | "number"
    | "boolean"
    | "select"
    | "text"
    | "date"
    | "article_search"
    | "category_search"
    | "client_search"
    | "segment_search"
    | "brand_search"
    | "entity_modal_selector";
  entityType?: "client" | "segment" | "category" | "brand";
  required?: boolean;
  min?: number;
  max?: number;
  options?: { value: any; label: string }[];
  placeholder?: string;
  helpText?: string;
}

interface DynamicDataScienceJobFormProps {
  isEdit?: boolean;
}

const DynamicDataScienceJobForm: React.FC<DynamicDataScienceJobFormProps> = ({
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { jobType, id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [jobConfig, setJobConfig] = useState<JobTypeConfig | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [segments, setSegments] = useState<any[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const workingOrganizationId =
    hasWorkingOrganization() && getSelectedOrganizationId()
      ? Number(getSelectedOrganizationId())
      : undefined;
  // Schedule builder state
  const [scheduleMode, setScheduleMode] = useState<
    "daily" | "weekly" | "monthly" | "advanced"
  >("daily");
  const [scheduleTime, setScheduleTime] = useState<Moment | null>(
    moment().hour(2).minute(0).second(0)
  );
  const [scheduleWeekdays, setScheduleWeekdays] = useState<string[]>(["MON"]);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState<number>(1);
  const [advancedCronValue, setAdvancedCronValue] = useState<string>("");
  const [selectorModalVisible, setSelectorModalVisible] = useState(false);
  const [selectorField, setSelectorField] = useState<ParameterField | null>(null);
  const [selectorRows, setSelectorRows] = useState<any[]>([]);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [selectorSearch, setSelectorSearch] = useState("");
  const [selectorSelectedIds, setSelectorSelectedIds] = useState<number[]>([]);
  const [selectorPagination, setSelectorPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectorLabelsByKey, setSelectorLabelsByKey] = useState<
    Record<string, Record<number, string>>
  >({
    client: {},
    segment: {},
    category: {},
    brand: {},
  });
  const [, setSelectorRenderVersion] = useState(0);

  const handleOrganizationSearch = async (searchText: string) => {
    if (searchText && searchText.length >= 2) {
      try {
        setLoading(true);
        const orgs = await searchOrganizations(searchText);
        setOrganizations(orgs);
      } catch (error) {
        console.error("Error searching organizations:", error);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    } else if (searchText.length === 0) {
      setOrganizations([]);
    }
  };

  const handleArticleSearch = async (searchText: string) => {
    const organizationId = form.getFieldValue("organization_id");
    if (searchText && searchText.length >= 2 && organizationId) {
      try {
        const arts = await searchArticles(searchText, organizationId);
        setArticles(arts.articles || []);
      } catch (error) {
        console.error("Error searching articles:", error);
        setArticles([]);
      }
    } else if (searchText.length === 0) {
      setArticles([]);
    }
  };

  const normalizeIdArray = (value: any): number[] => {
    if (value === undefined || value === null || value === "") {
      return [];
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
    }
    return String(value)
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .split(",")
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item));
  };

  const getEntityId = (record: any): number => Number(record.id);

  const getEntityLabel = (entityType: ParameterField["entityType"], record: any) => {
    if (!record) {
      return "";
    }
    if (entityType === "client") {
      const secondary = record.idNumber || record.email;
      return [record.name, secondary].filter(Boolean).join(" - ") || `ID: ${record.id}`;
    }
    return record.name || `ID: ${record.id}`;
  };

  const rememberSelectorLabels = (
    entityType: ParameterField["entityType"],
    rows: any[]
  ) => {
    if (!entityType || rows.length === 0) {
      return;
    }
    setSelectorLabelsByKey((prev) => {
      const nextLabels = { ...(prev[entityType] || {}) };
      rows.forEach((row) => {
        nextLabels[getEntityId(row)] = getEntityLabel(entityType, row);
      });
      return { ...prev, [entityType]: nextLabels };
    });
    setSelectorRenderVersion((version) => version + 1);
  };

  const hydrateBrandLabels = async (brandIds: number[]) => {
    const missingBrandIds = brandIds.filter(
      (brandId) => !selectorLabelsByKey.brand[brandId]
    );
    if (missingBrandIds.length === 0) {
      return;
    }

    const loadedBrands = await Promise.all(
      missingBrandIds.map(async (brandId) => {
        try {
          const response = await BrightMarketApi.getBrandById(brandId);
          return response?.data ?? null;
        } catch (error) {
          console.error("Error loading brand label:", error);
          return null;
        }
      })
    );

    rememberSelectorLabels("brand", loadedBrands.filter(Boolean));
  };

  const hydrateLoadedSelectorLabels = async (parameters: any) => {
    const brandIds = [
      ...normalizeIdArray(parameters?.brands),
      ...normalizeIdArray(parameters?.brand_id),
      ...normalizeIdArray(parameters?.brand_ids),
    ];
    await hydrateBrandLabels(Array.from(new Set(brandIds)));
  };

  const getSelectedLabels = (field: ParameterField) => {
    const entityType = field.entityType;
    const selectedIds = normalizeIdArray(form.getFieldValue(["parameters", field.name]));
    if (!entityType) {
      return selectedIds.map((selectedId) => `ID: ${selectedId}`);
    }
    const labels = selectorLabelsByKey[entityType] || {};
    return selectedIds.map((selectedId) => labels[selectedId] || `ID: ${selectedId}`);
  };

  const setParameterIds = (fieldName: string, ids: number[]) => {
    const parameters = form.getFieldValue("parameters") || {};
    form.setFieldsValue({
      parameters: {
        ...parameters,
        [fieldName]: ids,
      },
    });
    setSelectorRenderVersion((version) => version + 1);
  };

  const fetchSelectorRows = async (
    field: ParameterField,
    page = 1,
    searchText = selectorSearch
  ) => {
    if (!field.entityType) {
      return;
    }

    const organizationId = form.getFieldValue("organization_id") || workingOrganizationId;
    if ((field.entityType === "client" || field.entityType === "brand") && !organizationId) {
      setSelectorRows([]);
      setSelectorPagination((prev) => ({ ...prev, current: page, total: 0 }));
      return;
    }

    try {
      setSelectorLoading(true);
      let rows: any[] = [];
      let total = 0;

      if (field.entityType === "client") {
        const numericSearch = searchText && /^\d+$/.test(searchText.trim());
        const response = await BrightMarketApi.getClients(String(organizationId), page, {
          clientId: numericSearch ? searchText.trim() : undefined,
          name: !numericSearch ? searchText : undefined,
        });
        rows = response._embedded?.clients || [];
        total = response.totalElements || rows.length;
      } else if (field.entityType === "segment") {
        const response = await getClientSegmentsFiltered(page, { name: searchText });
        rows = response.clientSegments || [];
        total = response.totalElements || rows.length;
      } else if (field.entityType === "category") {
        rows = (await getCategoriesFiltered(searchText)) || [];
        total = rows.length;
      } else if (field.entityType === "brand") {
        const response = await BrightMarketApi.getBrands(String(organizationId), page, {
          name: searchText,
          externalId: "",
        });
        rows = response.brands?.brands || [];
        total = response.totalElements || rows.length;
      }

      setSelectorRows(rows);
      rememberSelectorLabels(field.entityType, rows);
      setSelectorPagination((prev) => ({ ...prev, current: page, total }));
    } catch (error) {
      console.error("Error loading selector rows:", error);
      setSelectorRows([]);
      setSelectorPagination((prev) => ({ ...prev, current: page, total: 0 }));
    } finally {
      setSelectorLoading(false);
    }
  };

  const openSelectorModal = (field: ParameterField) => {
    setSelectorField(field);
    setSelectorSearch("");
    setSelectorSelectedIds(normalizeIdArray(form.getFieldValue(["parameters", field.name])));
    setSelectorModalVisible(true);
    setSelectorPagination((prev) => ({ ...prev, current: 1 }));
    fetchSelectorRows(field, 1, "");
  };

  const closeSelectorModal = () => {
    setSelectorModalVisible(false);
    setSelectorField(null);
    setSelectorRows([]);
    setSelectorSearch("");
    setSelectorSelectedIds([]);
  };

  const confirmSelectorModal = () => {
    if (selectorField) {
      setParameterIds(selectorField.name, selectorSelectedIds);
    }
    closeSelectorModal();
  };

  const renderEntityModalSelector = (field: ParameterField) => {
    const selectedIds = normalizeIdArray(form.getFieldValue(["parameters", field.name]));
    const selectedLabels = getSelectedLabels(field);
    const selectedOptions = selectedIds.map((selectedId, index) => ({
      id: selectedId,
      label: selectedLabels[index] || `ID: ${selectedId}`,
    }));

    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          key={`${field.name}-${selectedOptions.map((option) => option.label).join("|")}`}
          mode="multiple"
          value={selectedIds}
          placeholder={t("DataScienceJobs-NoSelection")}
          open={false}
          showSearch={false}
          allowClear={false}
          style={{ width: "100%" }}
          tagRender={(props) => {
            const value = Number(props.value);
            const label =
              selectedOptions.find((option) => option.id === value)?.label || props.label;
            return (
              <Tag
                closable
                onClose={(event) => {
                  event.preventDefault();
                  setParameterIds(
                    field.name,
                    selectedIds.filter((selectedId) => selectedId !== value)
                  );
                }}
                style={{ marginRight: 4 }}
              >
                {label}
              </Tag>
            );
          }}
        >
          {selectedOptions.map((option) => (
            <Select.Option value={option.id} key={option.id}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Button onClick={() => openSelectorModal(field)}>
          {t("DataScienceJobs-SelectItems")}
        </Button>
      </Space>
    );
  };

  const getSelectorColumns = () => {
    if (selectorField?.entityType === "client") {
      return [
        { title: "ID", dataIndex: "id", key: "id", width: 90 },
        { title: t("Common-Name"), dataIndex: "name", key: "name" },
        { title: t("Common-Email"), dataIndex: "email", key: "email" },
        { title: t("Common-DocumentNumber"), dataIndex: "idNumber", key: "idNumber" },
      ];
    }

    return [
      { title: "ID", dataIndex: "id", key: "id", width: 90 },
      { title: t("Common-Name"), dataIndex: "name", key: "name" },
    ];
  };

  const handleBrandSearch = async (searchText: string = "") => {
    const organizationId = form.getFieldValue("organization_id") || workingOrganizationId;
    if (!organizationId) {
      setBrands([]);
      return;
    }

    try {
      setBrandsLoading(true);
      const response = await BrightMarketApi.getBrands(String(organizationId), 1, {
        name: searchText,
        externalId: "",
      });
      setBrands(response.brands?.brands || []);
    } catch (error) {
      console.error("Error searching brands:", error);
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  };

  const handleCategorySearch = async (searchText: string = "") => {
    try {
      setCategoriesLoading(true);
      const response = await getCategoriesFiltered(searchText);
      setCategories(response || []);
    } catch (error) {
      console.error("Error searching categories:", error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleClientSearch = async (searchText: string = "") => {
    const organizationId = form.getFieldValue("organization_id") || workingOrganizationId;
    if (!organizationId) {
      setClients([]);
      return;
    }

    try {
      setClientsLoading(true);
      const numericSearch = searchText && /^\d+$/.test(searchText.trim());
      const response = await BrightMarketApi.getClients(String(organizationId), 1, {
        clientId: numericSearch ? searchText.trim() : undefined,
        name: !numericSearch ? searchText : undefined,
      });
      setClients(response._embedded?.clients || []);
    } catch (error) {
      console.error("Error searching clients:", error);
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleSegmentSearch = async (searchText: string = "") => {
    try {
      setSegmentsLoading(true);
      const response = await getClientSegmentsFiltered(1, { name: searchText });
      setSegments(response.clientSegments || []);
    } catch (error) {
      console.error("Error searching segments:", error);
      setSegments([]);
    } finally {
      setSegmentsLoading(false);
    }
  };

  const getBrandOptions = (parameterName: string) => {
    const selectedBrandIds = normalizeIdArray(
      form.getFieldValue(["parameters", parameterName])
    );
    const brandsById = new Map<number, any>();
    brands.forEach((brand) => brandsById.set(Number(brand.id), brand));

    selectedBrandIds.forEach((brandId) => {
      if (!brandsById.has(brandId)) {
        brandsById.set(brandId, { id: brandId, name: `ID: ${brandId}` });
      }
    });

    return Array.from(brandsById.values()).map((brand) => (
      <Select.Option value={Number(brand.id)} key={brand.id}>
        {brand.name || `ID: ${brand.id}`}
      </Select.Option>
    ));
  };

  const getCategoryOptions = (parameterName: string) => {
    const selectedCategoryIds = normalizeIdArray(
      form.getFieldValue(["parameters", parameterName])
    );
    const categoriesById = new Map<number, any>();
    categories.forEach((category) => categoriesById.set(Number(category.id), category));

    selectedCategoryIds.forEach((categoryId) => {
      if (!categoriesById.has(categoryId)) {
        categoriesById.set(categoryId, { id: categoryId, name: `ID: ${categoryId}` });
      }
    });

    return Array.from(categoriesById.values()).map((category) => (
      <Select.Option value={Number(category.id)} key={category.id}>
        {category.name || `ID: ${category.id}`}
      </Select.Option>
    ));
  };

  const getClientOptions = (parameterName: string) => {
    const selectedClientIds = normalizeIdArray(
      form.getFieldValue(["parameters", parameterName])
    );
    const clientsById = new Map<number, any>();
    clients.forEach((client) => clientsById.set(Number(client.id), client));

    selectedClientIds.forEach((clientId) => {
      if (!clientsById.has(clientId)) {
        clientsById.set(clientId, { id: clientId, name: `ID: ${clientId}` });
      }
    });

    return Array.from(clientsById.values()).map((client) => (
      <Select.Option value={Number(client.id)} key={client.id}>
        {client.name || client.email || client.idNumber || `ID: ${client.id}`}
      </Select.Option>
    ));
  };

  const getSegmentOptions = (parameterName: string) => {
    const selectedSegmentIds = normalizeIdArray(
      form.getFieldValue(["parameters", parameterName])
    );
    const segmentsById = new Map<number, any>();
    segments.forEach((segment) => segmentsById.set(Number(segment.id), segment));

    selectedSegmentIds.forEach((segmentId) => {
      if (!segmentsById.has(segmentId)) {
        segmentsById.set(segmentId, { id: segmentId, name: `ID: ${segmentId}` });
      }
    });

    return Array.from(segmentsById.values()).map((segment) => (
      <Select.Option value={Number(segment.id)} key={segment.id}>
        {segment.name || `ID: ${segment.id}`}
      </Select.Option>
    ));
  };

  useEffect(() => {
    if (jobType) {
      setJobConfig(getJobTypeConfig(jobType));
    }
    if (isEdit && id) {
      loadJob();
    } else if (workingOrganizationId) {
      form.setFieldsValue({ organization_id: workingOrganizationId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobType, isEdit, id, workingOrganizationId, form]);

  // Non-schedulable job types (e.g. commercial_opportunities, activation_actions) can only be run
  // manually, so force scheduling off and clear any cron expression.
  useEffect(() => {
    const schedulable =
      getDataScienceJobTypes(t).find((item) => item.jobType === jobType)
        ?.schedulable !== false;
    if (!schedulable) {
      setScheduleEnabled(false);
      form.setFieldsValue({ cron_expression: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobType, t, form]);

  // Initialize schedule builder with default values
  useEffect(() => {
    if (!isEdit && jobConfig && scheduleEnabled) {
      const defaultCron =
        jobType === "client_metrics_refresh" ? "0 0 3 * * ?" : "0 0 2 * * ?";
      form.setFieldsValue({ cron_expression: defaultCron });
      setAdvancedCronValue(defaultCron);
      parseCronToScheduleBuilder(defaultCron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobConfig, isEdit, form, scheduleEnabled, jobType]);

  // Sync advancedCronValue with form value when switching to advanced mode
  useEffect(() => {
    if (scheduleMode === "advanced") {
      const currentCron = form.getFieldValue("cron_expression");
      if (currentCron && currentCron !== advancedCronValue) {
        setAdvancedCronValue(currentCron);
      }
    }
  }, [scheduleMode, form, advancedCronValue]);

  const getCronExamples = () => [
    {
      value: "0 0 2 * * ?",
      label: t("DataScienceJobs-CronExamples.daily_2am"),
    },
    {
      value: "0 30 9 * * ?",
      label: t("DataScienceJobs-CronExamples.daily_930am"),
    },
    {
      value: "0 0 14 * * ?",
      label: t("DataScienceJobs-CronExamples.daily_2pm"),
    },
    {
      value: "0 0 0 ? * SUN",
      label: t("DataScienceJobs-CronExamples.weekly_sunday"),
    },
    {
      value: "0 0 9 ? * MON",
      label: t("DataScienceJobs-CronExamples.weekly_monday"),
    },
    {
      value: "0 0 0 1 * ?",
      label: t("DataScienceJobs-CronExamples.monthly_1st"),
    },
    {
      value: "0 0 0 15 * ?",
      label: t("DataScienceJobs-CronExamples.monthly_15th"),
    },
    { value: "0 0 * * * ?", label: t("DataScienceJobs-CronExamples.hourly") },
    {
      value: "0 0 */6 * * ?",
      label: t("DataScienceJobs-CronExamples.every_6_hours"),
    },
    {
      value: "0 0 0 ? * MON-FRI",
      label: t("DataScienceJobs-CronExamples.weekdays"),
    },
    {
      value: "0 */5 * * * ?",
      label: t("DataScienceJobs-CronExamples.every_5_minutes"),
    },
  ];

  const generateCronExpression = (
    mode: string,
    time: Moment | null,
    weekdays: string[] = [],
    dayOfMonth: number = 1
  ): string => {
    if (!time) {
      return "";
    }
    const HH = String(time.hour());
    const mm = String(time.minute());
    let cron = "";

    if (mode === "daily") {
      cron = `0 ${mm} ${HH} * * ?`;
    } else if (mode === "weekly") {
      // For 6-field cron with 0=Sun .. 6=Sat we map MON.. to 1..6..0
      const map: Record<string, number> = {
        SUN: 0,
        MON: 1,
        TUE: 2,
        WED: 3,
        THU: 4,
        FRI: 5,
        SAT: 6,
      };
      const days = weekdays
        .map((w) => {
          const mapped = map[w];
          return mapped;
        })
        .filter((d) => d !== undefined)
        .join(",");
      if (!days) {
        cron = `0 ${mm} ${HH} ? * 1`; // Default to Monday
      } else {
        cron = `0 ${mm} ${HH} ? * ${days}`;
      }
    } else if (mode === "monthly") {
      cron = `0 ${mm} ${HH} ${dayOfMonth} * ?`;
    }
    return cron;
  };

  const updateCronFromBuilder = (
    mode = scheduleMode,
    time: Moment | null = scheduleTime,
    weekdays = scheduleWeekdays,
    dayOfMonth = scheduleDayOfMonth
  ) => {
    const cron = generateCronExpression(mode, time, weekdays, dayOfMonth);
    if (cron) {
      form.setFieldsValue({ cron_expression: cron });
    }
  };

  const parseCronToScheduleBuilder = useCallback((cronExpression: string) => {
    try {
      const parts = cronExpression.trim().split(/\s+/);

      if (parts.length !== 6) {
        setScheduleMode("advanced");
        return;
      }

      const [, minute, hour, dayOfMonth, , dayOfWeek] = parts;

      const isAdvancedPattern =
        minute.startsWith("*/") ||
        hour.startsWith("*/") ||
        hour.includes("-") ||
        hour.includes(",") ||
        cronExpression.includes(":");

      if (isAdvancedPattern) {
        setScheduleMode("advanced");
        return;
      }

      const time = moment()
        .hour(parseInt(hour))
        .minute(parseInt(minute))
        .second(0);
      setScheduleTime(time);

      if (dayOfMonth === "*" && dayOfWeek === "?") {
        setScheduleMode("daily");
      } else if (dayOfMonth === "?" && dayOfWeek !== "*" && dayOfWeek !== "?") {
        setScheduleMode("weekly");
        const dayMap: Record<number, string> = {
          0: "SUN",
          1: "MON",
          2: "TUE",
          3: "WED",
          4: "THU",
          5: "FRI",
          6: "SAT",
        };
        const weekdays = dayOfWeek
          .split(",")
          .map((d) => dayMap[parseInt(d)])
          .filter(Boolean);
        setScheduleWeekdays(weekdays);
      } else if (
        dayOfMonth !== "*" &&
        dayOfMonth !== "?" &&
        dayOfWeek === "?"
      ) {
        setScheduleMode("monthly");
        setScheduleDayOfMonth(parseInt(dayOfMonth));
      } else {
        setScheduleMode("advanced");
      }
    } catch (error) {
      console.error("Error parsing cron expression:", error);
      // Default to advanced if parsing fails
      setScheduleMode("advanced");
    }
  }, []);

  const getJobTypeConfig = (type: string): JobTypeConfig => {
    const configs = {
      demand_forecast: {
        displayName: t("DataScienceJobs-DemandForecastTitle"),
        description: t("DataScienceJobs-DemandForecastDescription"),
        parameters: {
          forecast_periods: 15,
          starting_date: "",
          split_by_sku: true,
          output_type: "database",
        },
        parameterFields: [
          {
            name: "forecast_periods",
            label: t("DataScienceJobs-ForecastPeriods"),
            type: "number" as const,
            required: true,
            min: 1,
            max: 365,
            helpText: t("DataScienceJobs-ForecastPeriodsHelp"),
          },
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "text" as const,
            required: false,
            placeholder: "YYYY-MM-DD",
            helpText: t("DataScienceJobs-DemandForecastStartingDateHelp"),
          },
          {
            name: "split_by_sku",
            label: t("DataScienceJobs-SplitBySku"),
            type: "boolean" as const,
            helpText: t("DataScienceJobs-SplitBySkuHelp"),
          },
        ],
      },
      assortment_optimization: {
        displayName: t("DataScienceJobs-AssortmentOptimizationTitle"),
        description: t("DataScienceJobs-AssortmentOptimizationDescription"),
        parameters: {
          use_mock_data: false,
          objective: "profit",
          output_type: "database",
        },
        parameterFields: [
          {
            name: "objective",
            label: t("DataScienceJobs-OptimizationObjective"),
            type: "select" as const,
            required: true,
            options: [
              { value: "profit", label: t("DataScienceJobs-MaximizeMargin") },
              {
                value: "rotation",
                label: t("DataScienceJobs-MaximizeQuantity"),
              },
            ],
          },
        ],
      },
      out_of_stock: {
        displayName: t("DataScienceJobs-OutOfStockTitle"),
        description: t("DataScienceJobs-OutOfStockDescription"),
        parameters: {
          output_type: "database",
        },
        parameterFields: [],
      },
      restock: {
        displayName: t("DataScienceJobs-RestockTitle"),
        description: t("DataScienceJobs-RestockDescription"),
        parameters: {
          n_restock_days: 2,
          exclusion_list: [],
          output_type: "database",
        },
        parameterFields: [
          {
            name: "n_restock_days",
            label: t("DataScienceJobs-RestockDays"),
            type: "number" as const,
            required: true,
            min: 1,
            max: 30,
            helpText: t("DataScienceJobs-RestockDaysHelp"),
          },
          {
            name: "exclusion_list",
            label: t("DataScienceJobs-ExcludeArticles"),
            type: "article_search" as const,
            helpText: t("DataScienceJobs-ExcludeArticlesHelp"),
          },
        ],
      },
      restock_manual_evaluation: {
        displayName: t("DataScienceJobs-RestockManualEvaluationTitle"),
        description: t("DataScienceJobs-RestockManualEvaluationDescription"),
        parameters: {
          starting_date: moment().subtract(30, "days").format("YYYY-MM-DD"),
          ending_date: moment().format("YYYY-MM-DD"),
          output_type: "database",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "text" as const,
            required: true,
            placeholder: "YYYY-MM-DD",
            helpText: t("DataScienceJobs-RestockManualEvaluationStartingDateHelp"),
          },
          {
            name: "ending_date",
            label: t("DataScienceJobs-EndingDate"),
            type: "text" as const,
            required: true,
            placeholder: "YYYY-MM-DD",
            helpText: t("DataScienceJobs-RestockManualEvaluationEndingDateHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      segments: {
        displayName: t("DataScienceJobs-SegmentsTitle"),
        description: t("DataScienceJobs-SegmentsDescription"),
        parameters: {
          output_type: "database",
          starting_date: "",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "text" as const,
            required: false,
            placeholder: "YYYY-MM-DD",
            helpText: t("DataScienceJobs-StartingDateHelp"),
          },
        ],
      },
      segment_engine: {
        displayName: t("DataScienceJobs-SegmentEngineTitle"),
        description: t("DataScienceJobs-SegmentEngineDescription"),
        parameters: {},
        parameterFields: [],
      },
      client_metrics_refresh: {
        displayName: t("DataScienceJobs-ClientMetricsRefreshTitle"),
        description: t("DataScienceJobs-ClientMetricsRefreshDescription"),
        parameters: {},
        parameterFields: [],
      },
      commercial_opportunities: {
        displayName: t("DataScienceJobs-CommercialOpportunitiesTitle"),
        description: t("DataScienceJobs-CommercialOpportunitiesDescription"),
        parameters: {},
        parameterFields: [],
      },
      activation_actions: {
        displayName: t("DataScienceJobs-ActivationActionsTitle"),
        description: t("DataScienceJobs-ActivationActionsDescription"),
        parameters: {},
        parameterFields: [],
      },
      forecast_manual_promotions: {
        displayName: t("DataScienceJobs-ForecastManualPromotionsTitle"),
        description: t("DataScienceJobs-ForecastManualPromotionsDescription"),
        parameters: {
          output_type: "database",
        },
        parameterFields: [
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
              { value: "csv", label: t("DataScienceJobs-OutputType.csv") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      promotions: {
        displayName: t("DataScienceJobs-PromotionsTitle"),
        description: t("DataScienceJobs-PromotionsDescription"),
        parameters: {
          days_in_advance: 7,
          discounts: "10,20,30",
          durations: "7,14,30",
          brand_id: "",
          category_id: "",
          adapt_to_commercial_dates: false,
          output_type: "database",
        },
        parameterFields: [
          {
            name: "days_in_advance",
            label: t("DataScienceJobs-DaysInAdvance"),
            type: "number" as const,
            required: true,
            min: 1,
            max: 365,
            helpText: t("DataScienceJobs-DaysInAdvanceHelp"),
          },
          {
            name: "discounts",
            label: t("DataScienceJobs-Discounts"),
            type: "text" as const,
            required: true,
            placeholder: "10,20,30",
            helpText: t("DataScienceJobs-DiscountsHelp"),
          },
          {
            name: "durations",
            label: t("DataScienceJobs-Durations"),
            type: "text" as const,
            required: true,
            placeholder: "7,14,30",
            helpText: t("DataScienceJobs-DurationsHelp"),
          },
          {
            name: "brand_id",
            label: t("DataScienceJobs-BrandIds"),
            type: "text" as const,
            required: false,
            placeholder: "50,51",
            helpText: t("DataScienceJobs-PromotionsBrandIdHelp"),
          },
          {
            name: "category_id",
            label: t("DataScienceJobs-CategoryIds"),
            type: "text" as const,
            required: false,
            placeholder: "10,20",
            helpText: t("DataScienceJobs-PromotionsCategoryIdHelp"),
          },
          {
            name: "top_n",
            label: t("DataScienceJobs-TopN"),
            type: "number" as const,
            required: false,
            min: 1,
            helpText: t("DataScienceJobs-TopNHelp"),
          },
          {
            name: "adapt_to_commercial_dates",
            label: t("DataScienceJobs-AdaptToCommercialDates"),
            type: "boolean" as const,
            helpText: t("DataScienceJobs-AdaptToCommercialDatesHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      price_elasticity: {
        displayName: t("DataScienceJobs-PriceElasticityTitle"),
        description: t("DataScienceJobs-PriceElasticityDescription"),
        parameters: {
          starting_date: moment().format("YYYY-MM-DD"),
          output_type: "database",
          brands: "",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "date" as const,
            required: true,
            helpText: t("DataScienceJobs-PriceElasticityStartingDateHelp"),
          },
          {
            name: "brands",
            label: t("DataScienceJobs-Brands"),
            type: "entity_modal_selector" as const,
            entityType: "brand" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectBrands"),
            helpText: t("DataScienceJobs-BrandsHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      price_cross_elasticity: {
        displayName: t("DataScienceJobs-PriceCrossElasticityTitle"),
        description: t("DataScienceJobs-PriceCrossElasticityDescription"),
        parameters: {
          starting_date: moment().format("YYYY-MM-DD"),
          output_type: "database",
          brands: "",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "date" as const,
            required: true,
            helpText: t("DataScienceJobs-PriceElasticityStartingDateHelp"),
          },
          {
            name: "brands",
            label: t("DataScienceJobs-Brands"),
            type: "entity_modal_selector" as const,
            entityType: "brand" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectBrands"),
            helpText: t("DataScienceJobs-PriceCrossElasticityBrandsHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      seasonal_forecast: {
        displayName: t("DataScienceJobs-SeasonalForecastTitle"),
        description: t("DataScienceJobs-SeasonalForecastDescription"),
        parameters: {
          starting_date: moment().format("YYYY-MM-DD"),
          brand_id: "",
          forecast_periods: 182,
          output_type: "database",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "date" as const,
            required: true,
            helpText: t("DataScienceJobs-SeasonalForecastStartingDateHelp"),
          },
          {
            name: "brand_id",
            label: t("DataScienceJobs-Brands"),
            type: "entity_modal_selector" as const,
            entityType: "brand" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectBrands"),
            helpText: t("DataScienceJobs-SeasonalForecastBrandIdHelp"),
          },
          {
            name: "forecast_periods",
            label: t("DataScienceJobs-ForecastPeriods"),
            type: "number" as const,
            required: true,
            min: 1,
            placeholder: "182",
            helpText: t("DataScienceJobs-SeasonalForecastPeriodsHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      next_purchase: {
        displayName: t("DataScienceJobs-NextPurchaseTitle"),
        description: t("DataScienceJobs-NextPurchaseDescription"),
        parameters: {
          starting_date: moment().format("YYYY-MM-DD"),
          output_type: "database",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "date" as const,
            required: true,
            helpText: t("DataScienceJobs-NextPurchaseStartingDateHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      market_basket: {
        displayName: t("DataScienceJobs-MarketBasketTitle"),
        description: t("DataScienceJobs-MarketBasketDescription"),
        parameters: {
          starting_date: moment().format("YYYY-MM-DD"),
          analysis_type: "multiproduct_category",
          output_type: "database",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "date" as const,
            required: true,
            helpText: t("DataScienceJobs-MarketBasketStartingDateHelp"),
          },
          {
            name: "analysis_type",
            label: t("DataScienceJobs-AnalysisType"),
            type: "select" as const,
            required: true,
            options: [
              {
                value: "multiproduct_category",
                label: t("DataScienceJobs-AnalysisType.multiproduct_category"),
              },
              {
                value: "multiproduct_brand",
                label: t("DataScienceJobs-AnalysisType.multiproduct_brand"),
              },
            ],
            helpText: t("DataScienceJobs-AnalysisTypeHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      store_transference: {
        displayName: t("DataScienceJobs-StoreTransferenceTitle"),
        description: t("DataScienceJobs-StoreTransferenceDescription"),
        parameters: {
          output_type: "database",
        },
        parameterFields: [
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-StoreTransferenceOutputTypeHelp"),
          },
        ],
      },
      propensity_to_buy: {
        displayName: t("DataScienceJobs-PropensityToBuyTitle"),
        description: t("DataScienceJobs-PropensityToBuyDescription"),
        parameters: {
          population_type: "client",
          client_id: "",
          segment_id: "",
          aggregation_level: "all",
          category_ids: "",
          brand_ids: "",
          output_type: "database",
        },
        parameterFields: [
          {
            name: "population_type",
            label: t("DataScienceJobs-PopulationType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "client", label: t("DataScienceJobs-PopulationType.client") },
              { value: "segment", label: t("DataScienceJobs-PopulationType.segment") },
            ],
            helpText: t("DataScienceJobs-PopulationTypeHelp"),
          },
          {
            name: "client_id",
            label: t("DataScienceJobs-Clients"),
            type: "entity_modal_selector" as const,
            entityType: "client" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectClients"),
            helpText: t("DataScienceJobs-ClientIdsHelp"),
          },
          {
            name: "segment_id",
            label: t("DataScienceJobs-Segments"),
            type: "entity_modal_selector" as const,
            entityType: "segment" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectSegments"),
            helpText: t("DataScienceJobs-SegmentIdsHelp"),
          },
          {
            name: "aggregation_level",
            label: t("DataScienceJobs-AggregationLevel"),
            type: "select" as const,
            required: true,
            options: [
              { value: "all", label: t("DataScienceJobs-AggregationLevel.all") },
              { value: "category", label: t("DataScienceJobs-AggregationLevel.category") },
              { value: "brand", label: t("DataScienceJobs-AggregationLevel.brand") },
              {
                value: "category_brand",
                label: t("DataScienceJobs-AggregationLevel.category_brand"),
              },
            ],
            helpText: t("DataScienceJobs-AggregationLevelHelp"),
          },
          {
            name: "category_ids",
            label: t("DataScienceJobs-Categories"),
            type: "entity_modal_selector" as const,
            entityType: "category" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectCategories"),
            helpText: t("DataScienceJobs-CategoryIdsHelp"),
          },
          {
            name: "brand_ids",
            label: t("DataScienceJobs-Brands"),
            type: "entity_modal_selector" as const,
            entityType: "brand" as const,
            required: false,
            placeholder: t("DataScienceJobs-SelectBrands"),
            helpText: t("DataScienceJobs-BrandIdsHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
      rewards: {
        displayName: t("DataScienceJobs-RewardsTitle"),
        description: t("DataScienceJobs-RewardsDescription"),
        parameters: {},
        parameterFields: [],
      },
      demand_forecast_cleanup: {
        displayName: t("DataScienceJobs-DemandForecastCleanupTitle"),
        description: t("DataScienceJobs-DemandForecastCleanupDescription"),
        parameters: {},
        parameterFields: [],
      },
      client_elasticity: {
        displayName: t("DataScienceJobs-ClientElasticityTitle"),
        description: t("DataScienceJobs-ClientElasticityDescription"),
        parameters: {
          output_type: "database",
        },
        parameterFields: [
          {
            name: "starting_date",
            label: t("DataScienceJobs-StartingDate"),
            type: "text" as const,
            required: false,
            placeholder: "YYYY-MM-DD",
            helpText: t("DataScienceJobs-StartingDateHelp"),
          },
          {
            name: "output_type",
            label: t("DataScienceJobs-OutputType"),
            type: "select" as const,
            required: true,
            options: [
              { value: "database", label: t("DataScienceJobs-OutputType.database") },
              { value: "none", label: t("DataScienceJobs-OutputType.none") },
              { value: "csv", label: t("DataScienceJobs-OutputType.csv") },
            ],
            helpText: t("DataScienceJobs-OutputTypeHelp"),
          },
        ],
      },
    };

    return (
      configs[type as keyof typeof configs] || {
        displayName: type,
        description: "",
        parameters: {},
        parameterFields: [],
      }
    );
  };

  const loadJob = async () => {
    try {
      setLoading(true);
      const job = await DataScienceJobApi.getJob(parseInt(id!));

      let parsedParameters: any = {};
      if (typeof job.parameters === "string") {
        try {
          parsedParameters = JSON.parse(job.parameters);
        } catch (error) {
          console.error("Error parsing parameters from string:", error);
          parsedParameters = {};
        }
      } else if (
        typeof job.parameters === "object" &&
        job.parameters !== null
      ) {
        parsedParameters = job.parameters;
      } else {
        parsedParameters = {};
      }

      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
        if (job.organization_id) {
          orgs.find((org) => org.id === job.organization_id);
        }
      } catch (error) {
        console.error("Error loading organizations:", error);
      }

      form.setFieldsValue({
        job_name: job.job_name,
        organization_id: job.organization_id,
        is_active: job.is_active,
        cron_expression: job.cron_expression,
        parameters: parsedParameters,
      });
      await hydrateLoadedSelectorLabels(parsedParameters);

      setScheduleEnabled(Boolean(job.cron_expression?.trim()));
      setAdvancedCronValue(job.cron_expression || "");

      if (job.cron_expression) {
        parseCronToScheduleBuilder(job.cron_expression!);
      }
    } catch (error) {
      message.error(t("DataScienceJobs-LoadJobError"));
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      let finalCronExpression = "";
      if (scheduleEnabled) {
        finalCronExpression = values.cron_expression;
        if (scheduleMode !== "advanced" && scheduleTime) {
          finalCronExpression = generateCronExpression(
            scheduleMode,
            scheduleTime,
            scheduleWeekdays,
            scheduleDayOfMonth
          );
        }

        if (!finalCronExpression || finalCronExpression.includes("? ?")) {
          finalCronExpression = "0 0 2 * * ?"; // Default daily at 2 AM
        }
      }

      form.setFieldsValue({ cron_expression: finalCronExpression });

      const jobData: Partial<DataScienceJob> = {
        job_name: values.job_name,
        job_type: jobType!,
        is_active: values.is_active,
        cron_expression: finalCronExpression,
        parameters: JSON.stringify(values.parameters || {}),
      };

      if (values.organization_id) {
        jobData.organization_id = values.organization_id;
      } else if (workingOrganizationId) {
        jobData.organization_id = workingOrganizationId;
      }

      if (isEdit) {
        await DataScienceJobApi.updateJob(parseInt(id!), jobData);
        message.success(t("DataScienceJobs-UpdateJobSuccess"));
      } else {
        await DataScienceJobApi.createJob(jobData as any);
        message.success(t("DataScienceJobs-CreateJobSuccess"));
      }

      navigate(`/admin/datasciencejobs/${jobType}`);
    } catch (error) {
      message.error(
        getApiErrorMessage(
          error,
          isEdit
            ? t("DataScienceJobs-UpdateJobError")
            : t("DataScienceJobs-CreateJobError")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  if (!jobConfig) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
            <div style={{ marginTop: 16 }}>{t("Common-Loading")}</div>
          </div>
        </Card>
      </div>
    );
  }

  const isDataScienceEcsJob =
    getDataScienceJobTypes(t).find((item) => item.jobType === jobType)?.origin ===
    "data_science";

  const isCurrentJobTypeSchedulable =
    getDataScienceJobTypes(t).find((item) => item.jobType === jobType)
      ?.schedulable !== false;

  return (
    <AdminValidation>
      <div style={{ padding: "24px" }}>
        <Card>
          <Title level={2}>
            {isEdit
              ? t("DataScienceJobs-EditJob")
              : t("DataScienceJobs-NewJob")}{" "}
            - {jobConfig.displayName}
          </Title>
          <Text type="secondary">{jobConfig.description}</Text>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              is_active: true,
              job_type: jobType,
              cron_expression: "0 0 2 * * ?",
              parameters: {
                ...jobConfig.parameters,
                task_profile: getRecommendedTaskProfile(jobType || ""),
              },
            }}
          >
            <Form.Item
              name="job_name"
              label={t("DataScienceJobs-JobName")}
              rules={[
                { required: true, message: t("Common-RequiredField") },
                { min: 3, message: t("Common-MinLength3") },
              ]}
            >
              <Input placeholder={t("DataScienceJobs-JobName")} />
            </Form.Item>

            {/* Job type is auto-filled and hidden since it's shown in the title */}
            {!isEdit && (
              <Form.Item name="job_type" hidden>
                <Input />
              </Form.Item>
            )}

            {workingOrganizationId ? (
              <Form.Item name="organization_id" hidden>
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                name="organization_id"
                label={t("DataScienceJobs-Organization")}
                rules={[{ required: true, message: t("Common-RequiredField") }]}
              >
                <Select
                  placeholder={t("DataScienceJobs-Organization")}
                  showSearch
                  allowClear
                  loading={loading}
                  onSearch={handleOrganizationSearch}
                  onChange={() => {
                    setBrands([]);
                    setClients([]);
                    const parameters = form.getFieldValue("parameters") || {};
                    form.setFieldsValue({
                      parameters: {
                        ...parameters,
                        brands: [],
                        brand_ids: [],
                        client_id: [],
                      },
                    });
                  }}
                  filterOption={false}
                  notFoundContent={
                    organizations.length === 0
                      ? t("DataScienceJobs-TypeToSearch")
                      : t("DataScienceJobs-NoOrganizationsFound")
                  }
                  onFocus={() => {
                    // Trigger search with empty string to show "type to search" message
                    if (organizations.length === 0) {
                      handleOrganizationSearch("");
                    }
                  }}
                >
                  {organizations.map((org) => (
                    <Select.Option key={org.id} value={org.id}>
                      {org.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {!jobConfig.parameterFields.some(
              (field) => field.name === "output_type"
            ) && (
              <Form.Item
                name={["parameters", "output_type"]}
                label={t("DataScienceJobs-OutputType")}
                rules={[{ required: true, message: t("Common-RequiredField") }]}
              >
                <Select>
                  <Select.Option value="database">
                    {t("DataScienceJobs-OutputType.database")}
                  </Select.Option>
                  <Select.Option value="none">
                    {t("DataScienceJobs-OutputType.none")}
                  </Select.Option>
                </Select>
              </Form.Item>
            )}

            <Row gutter={24}>
              {isDataScienceEcsJob && (
                <Col span={8}>
                  <Form.Item
                    name={["parameters", "task_profile"]}
                    label={t("DataScienceJobs-TaskProfile")}
                    rules={[
                      { required: true, message: t("Common-RequiredField") },
                    ]}
                    getValueFromEvent={() =>
                      form.getFieldValue(["parameters", "task_profile"])
                    }
                  >
                    <Select
                      options={buildTaskProfileSelectOptions(t, jobType || "")}
                      onChange={(value: TaskProfileValue) => {
                        const applyProfile = (profile: TaskProfileValue) => {
                          form.setFieldsValue({
                            parameters: {
                              ...(form.getFieldValue("parameters") || {}),
                              task_profile: profile,
                            },
                          });
                        };

                        if (!isHighResourceTaskProfile(value)) {
                          applyProfile(value);
                          return;
                        }

                        Modal.confirm({
                          title: t(
                            "DataScienceJobs-TaskProfile.HighResourceConfirmTitle"
                          ),
                          content: t(
                            "DataScienceJobs-TaskProfile.HighResourceConfirmContent",
                            {
                              profile: getTaskProfileBaseLabel(t, value),
                            }
                          ),
                          okText: t(
                            "DataScienceJobs-TaskProfile.HighResourceConfirmOk"
                          ),
                          cancelText: t(
                            "DataScienceJobs-TaskProfile.HighResourceConfirmCancel"
                          ),
                          onOk: () => applyProfile(value),
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={isDataScienceEcsJob ? 8 : 12}>
                <Form.Item
                  name="is_active"
                  label={t("DataScienceJobs-IsActive")}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              {isCurrentJobTypeSchedulable && (
                <Col span={isDataScienceEcsJob ? 8 : 12}>
                  <Form.Item label={t("DataScienceJobs-EnableSchedule")}>
                    <Switch
                      checked={scheduleEnabled}
                      onChange={(checked) => {
                        setScheduleEnabled(checked);
                        if (checked) {
                          const cron =
                            form.getFieldValue("cron_expression") ||
                            "0 0 2 * * ?";
                          form.setFieldsValue({ cron_expression: cron });
                          setAdvancedCronValue(cron);
                          parseCronToScheduleBuilder(cron);
                        } else {
                          form.setFieldsValue({ cron_expression: "" });
                          setAdvancedCronValue("");
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {isCurrentJobTypeSchedulable && scheduleEnabled && (
              <Form.Item
                name="cron_expression"
                label={t("DataScienceJobs-CronExpression")}
                rules={[{ required: true, message: t("Common-RequiredField") }]}
              >
                <div>
                <Radio.Group
                  value={scheduleMode}
                  onChange={(e) => {
                    setScheduleMode(e.target.value);
                    updateCronFromBuilder(e.target.value);
                  }}
                  style={{ marginBottom: 12 }}
                >
                  <Radio.Button value="daily">{t("Common-Daily")}</Radio.Button>
                  <Radio.Button value="weekly">
                    {t("Common-Weekly")}
                  </Radio.Button>
                  <Radio.Button value="monthly">
                    {t("Common-Monthly")}
                  </Radio.Button>
                  <Radio.Button value="advanced">
                    {t("Common-Advanced")}
                  </Radio.Button>
                </Radio.Group>

                {scheduleMode !== "advanced" && (
                  <div
                    style={{
                      display: "flex",
                      gap: 24,
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 120,
                      }}
                    >
                      <Text strong style={{ marginBottom: 8 }}>
                        {t("Common-Hour")}
                      </Text>
                      <ConfigProvider locale={esES}>
                        <TimePicker
                          value={scheduleTime}
                          onChange={(v) => {
                            setScheduleTime(v);
                            updateCronFromBuilder(scheduleMode, v);
                          }}
                          format="HH:mm"
                          style={{ width: "100%" }}
                          placeholder={t("Common-SelectTime")}
                        />
                      </ConfigProvider>
                    </div>

                    {scheduleMode === "weekly" && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 220,
                        }}
                      >
                        <Text strong style={{ marginBottom: 8 }}>
                          {t("Common-Days")}
                        </Text>
                        <Select
                          mode="multiple"
                          value={scheduleWeekdays}
                          style={{ width: "100%" }}
                          onChange={(vals) => {
                            setScheduleWeekdays(vals);
                            updateCronFromBuilder(
                              scheduleMode,
                              scheduleTime,
                              vals
                            );
                          }}
                        >
                          {[
                            "SUN",
                            "MON",
                            "TUE",
                            "WED",
                            "THU",
                            "FRI",
                            "SAT",
                          ].map((d) => (
                            <Select.Option key={d} value={d}>
                              {t(`Common-DaysOfWeek.${d}`)}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    )}

                    {scheduleMode === "monthly" && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 120,
                        }}
                      >
                        <Text strong style={{ marginBottom: 8 }}>
                          {t("Common-DayOfMonth")}
                        </Text>
                        <InputNumber
                          min={1}
                          max={31}
                          value={scheduleDayOfMonth}
                          style={{ width: "100%" }}
                          onChange={(v) => {
                            const val = Number(v) || 1;
                            setScheduleDayOfMonth(val);
                            updateCronFromBuilder(
                              scheduleMode,
                              scheduleTime,
                              scheduleWeekdays,
                              val
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {scheduleMode === "advanced" && (
                  <div style={{ marginTop: 8 }}>
                    <Input
                      placeholder="Ej: 0 0 2 * * ? (Diario a las 2:00 AM)"
                      value={advancedCronValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAdvancedCronValue(value);
                        form.setFieldsValue({ cron_expression: value });
                      }}
                      style={{ width: "100%", marginBottom: 12 }}
                    />
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("DataScienceJobs-CronExamples")}:
                      </Text>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {getCronExamples().map((example) => (
                        <Button
                          key={example.value}
                          size="small"
                          type="dashed"
                          onClick={() => {
                            setAdvancedCronValue(example.value);
                            form.setFieldsValue({
                              cron_expression: example.value,
                            });
                          }}
                          style={{ marginBottom: 4 }}
                        >
                          {example.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              </Form.Item>
            )}

            <Form.Item
              name="parameters"
              label={t("DataScienceJobs-Parameters")}
              hidden
            >
              <Input />
            </Form.Item>

            {/* Dynamic Parameter Fields */}
            {jobConfig.parameterFields.map((field) => {
              const renderField = () => {
                switch (field.type) {
                  case "number":
                    return (
                      <InputNumber
                        placeholder={field.placeholder}
                        min={field.min}
                        max={field.max}
                        style={{ width: "100%" }}
                      />
                    );
                  case "boolean":
                    return <Switch />;
                  case "select":
                    return (
                      <Select
                        placeholder={field.placeholder}
                        showSearch
                        allowClear={!field.required}
                      >
                        {field.options?.map((option) => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  case "text":
                    return <Input placeholder={field.placeholder} />;
                  case "date":
                    return (
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder={field.placeholder || "DD/MM/YYYY"}
                      />
                    );
                  case "article_search":
                    return (
                      <Select
                        mode="multiple"
                        placeholder="Buscar artículos..."
                        showSearch
                        allowClear
                        filterOption={false}
                        onSearch={(value) => {
                          handleArticleSearch(value);
                        }}
                        notFoundContent={
                          articles.length === 0
                            ? t("DataScienceJobs-TypeToSearchArticles")
                            : t("DataScienceJobs-NoArticlesFound")
                        }
                      >
                        {articles.map((article) => (
                          <Select.Option key={article.id} value={article.id}>
                            {article.name}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  case "brand_search":
                    return (
                      <Select
                        mode="multiple"
                        placeholder={field.placeholder}
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={brandsLoading}
                        onSearch={handleBrandSearch}
                        onFocus={() => handleBrandSearch("")}
                        notFoundContent={
                          brandsLoading
                            ? t("Common-Loading")
                            : t("DataScienceJobs-NoBrandsFound")
                        }
                      >
                        {getBrandOptions(field.name)}
                      </Select>
                    );
                  case "category_search":
                    return (
                      <Select
                        mode="multiple"
                        placeholder={field.placeholder}
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={categoriesLoading}
                        onSearch={handleCategorySearch}
                        onFocus={() => handleCategorySearch("")}
                        notFoundContent={
                          categoriesLoading
                            ? t("Common-Loading")
                            : t("DataScienceJobs-NoCategoriesFound")
                        }
                      >
                        {getCategoryOptions(field.name)}
                      </Select>
                    );
                  case "client_search":
                    return (
                      <Select
                        mode="multiple"
                        placeholder={field.placeholder}
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={clientsLoading}
                        onSearch={handleClientSearch}
                        onFocus={() => handleClientSearch("")}
                        notFoundContent={
                          clientsLoading
                            ? t("Common-Loading")
                            : t("DataScienceJobs-NoClientsFound")
                        }
                      >
                        {getClientOptions(field.name)}
                      </Select>
                    );
                  case "segment_search":
                    return (
                      <Select
                        mode="multiple"
                        placeholder={field.placeholder}
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={segmentsLoading}
                        onSearch={handleSegmentSearch}
                        onFocus={() => handleSegmentSearch("")}
                        notFoundContent={
                          segmentsLoading
                            ? t("Common-Loading")
                            : t("DataScienceJobs-NoSegmentsFound")
                        }
                      >
                        {getSegmentOptions(field.name)}
                      </Select>
                    );
                  case "entity_modal_selector":
                    return renderEntityModalSelector(field);
                  default:
                    return null;
                }
              };

              return (
                <Form.Item
                  key={field.name}
                  name={["parameters", field.name]}
                  label={field.label}
                  getValueProps={
                    field.type === "date"
                      ? (value) => ({
                          value:
                            value && moment(value, "YYYY-MM-DD", true).isValid()
                              ? moment(value, "YYYY-MM-DD")
                              : null,
                        })
                      : [
                          "brand_search",
                          "category_search",
                          "client_search",
                          "segment_search",
                          "entity_modal_selector",
                        ].includes(field.type)
                      ? (value) => ({ value: normalizeIdArray(value) })
                      : undefined
                  }
                  getValueFromEvent={
                    field.type === "date"
                      ? (date: Moment | null) =>
                          date ? date.format("YYYY-MM-DD") : undefined
                      : undefined
                  }
                  rules={[
                    ...(field.required
                      ? [{ required: true, message: t("Common-RequiredField") }]
                      : []),
                    ...(field.type === "number" && field.min !== undefined
                      ? [
                          {
                            type: "number" as const,
                            min: field.min,
                            message: `Mínimo: ${field.min}`,
                          },
                        ]
                      : []),
                    ...(field.type === "number" && field.max !== undefined
                      ? [
                          {
                            type: "number" as const,
                            max: field.max,
                            message: `Máximo: ${field.max}`,
                          },
                        ]
                      : []),
                  ]}
                  help={field.helpText}
                >
                  {renderField()}
                </Form.Item>
              );
            })}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {isEdit ? t("Common-Update") : t("Common-Create")}
                </Button>
                <Button
                  onClick={() => navigate(`/admin/datasciencejobs/${jobType}`)}
                >
                  {t("Common-Cancel")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
        <Modal
          title={selectorField?.label || t("DataScienceJobs-SelectItems")}
          visible={selectorModalVisible}
          onCancel={closeSelectorModal}
          onOk={confirmSelectorModal}
          okText={t("DataScienceJobs-ConfirmSelection")}
          cancelText={t("Common-Cancel")}
          width={800}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input.Search
              allowClear
              placeholder={t("DataScienceJobs-SearchItems")}
              value={selectorSearch}
              onChange={(event) => setSelectorSearch(event.target.value)}
              onSearch={(value) => {
                if (selectorField) {
                  fetchSelectorRows(selectorField, 1, value);
                }
              }}
              enterButton={t("Common-Search")}
            />
            <Typography.Text type="secondary">
              {t("DataScienceJobs-SelectedCount", {
                count: selectorSelectedIds.length,
              })}
            </Typography.Text>
            <Table
              size="small"
              rowKey={(record) => getEntityId(record)}
              columns={getSelectorColumns()}
              dataSource={selectorRows}
              loading={selectorLoading}
              rowSelection={{
                selectedRowKeys: selectorSelectedIds,
                preserveSelectedRowKeys: true,
                onChange: (selectedRowKeys) => {
                  setSelectorSelectedIds(selectedRowKeys.map((key) => Number(key)));
                },
              }}
              pagination={{
                current: selectorPagination.current,
                pageSize: selectorPagination.pageSize,
                total: selectorPagination.total,
                showSizeChanger: false,
                onChange: (page) => {
                  if (selectorField) {
                    fetchSelectorRows(selectorField, page, selectorSearch);
                  }
                },
              }}
            />
          </Space>
        </Modal>
      </div>
    </AdminValidation>
  );
};

export default DynamicDataScienceJobForm;
